/**
 * Planning des projets (CDC §3.6, GELÉ) : logique pure, déterministe, sans fuseau.
 *
 * Portage en dates `AAAA-MM-JJ` du bloc « PROJETS » de la maquette `docs/maquette/maquette-v7.html`
 * (fonctions `simulate`, `weekLoad`, `workDay`, `overlapAbs`, `isOff`, `phaseWho`, `projSegments`).
 * La maquette compte en jours depuis le lundi 28/09/2026 et ses semaines commencent le lundi : ici, une
 * semaine est désignée par la date de son lundi. Les calculs passent par `dates.ts` (calendrier en UTC),
 * donc le résultat ne dépend pas du fuseau de la machine.
 *
 * Les gardes de boucle (80, 10, 40, 60) sont reprises telles quelles de la maquette.
 */

import { addDays, formatIsoDate, parseIsoDate } from './dates.js';

export type UserId = string;

export interface PlanningDurations {
  /** R3 → début de la maquette (jours calendaires). */
  r3ToMockup: number;
  /** Début de la maquette → R4. */
  mockupToR4: number;
  /** R4 → retours. */
  r4ToFeedback: number;
  /** Retours → intégration au plus tôt. */
  feedbackToIntegration: number;
}

export interface PlanningCapacity {
  /** Intégrations simultanées acceptées d'habitude par semaine. */
  usual: number;
  /** Maximum acceptable (alternative proposée par le simulateur). */
  max: number;
}

export interface PlanningRoles {
  /** Fait les maquettes (et les ajustements). */
  mockup: UserId;
  /** Tient les rendez-vous R3, R4 et R5. */
  meetings: UserId;
  /** Fait l'intégration. */
  integration: UserId;
}

export interface PlanningAbsence {
  userId: UserId;
  /** Premier jour d'absence (inclus). */
  from: string;
  /** Dernier jour d'absence (inclus). */
  to: string;
  label: string;
}

export interface ExistingIntegration {
  integrationStart: string;
  integrationWeeks: number;
}

/** Maquette d'un projet existant (lot AE) : elle compte dans la limite de sa semaine, sauf en sous-traitance. */
export interface ExistingMockup {
  /** Début de la maquette (`phases.Maquette`). */
  mockupStart: string;
  /** Maquette sous-traitée : ne compte pas dans la limite. */
  subcontracted?: boolean;
}

export interface SimulateInput {
  today: string;
  startNotBefore?: string;
  integrationWeeks: 1 | 2 | 3;
  subcontracted: boolean;
  durations: PlanningDurations;
  capacity: PlanningCapacity;
  roles: PlanningRoles;
  absences: PlanningAbsence[];
  /** Lundis des semaines de fermeture, ex. '2026-12-28'. */
  closedWeeks: string[];
  existing: ExistingIntegration[];
  /**
   * Lot AE : maquettes qui peuvent démarrer la même semaine (lundi), maquettes sous-traitées non comptées
   * (Réglages > Planning, 1 par défaut).
   */
  mockupsPerWeek: number;
  /**
   * Lot AE : écart maximum entre le R3 et le début de la maquette, en semaines (5 par défaut) ; le minimum est
   * `durations.r3ToMockup`. Au-delà, on oublie les infos précises pour faire la maquette.
   */
  r3ToMockupMaxWeeks: number;
  /** Lot AE : maquettes des projets existants (sous-traitées comprises : elles sont écartées du décompte). */
  existingMockups: ExistingMockup[];
}

export interface SimulateResult {
  r3: string;
  mockup: string;
  r4: string;
  feedback: string;
  /** Lundi de début de l'intégration. */
  integration: string;
  /** Mise en ligne. */
  r5: string;
  /** Explications en français, dans l'ordre de la maquette (vide : aucun conflit). */
  notes: string[];
  /** Semaines de décalage de l'intégration par rapport au plus tôt possible. */
  delayedWeeks: number;
  /** Lot AE : semaines de décalage de la maquette (semaines pleines, fermées, congés) par rapport au plus tôt. */
  mockupDelayedWeeks: number;
}

/** Options d'affichage (hors contrat gelé) : noms des personnes pour les notes. */
export interface SimulateOptions {
  /** Prénom affiché par identifiant ; à défaut, l'identifiant lui-même. */
  names?: Record<UserId, string>;
}

/** Charge par semaine : lundi `AAAA-MM-JJ` → nombre d'intégrations en cours cette semaine-là. */
export type WeekLoad = Record<string, number>;

export const DEFAULT_PLANNING_DURATIONS: Readonly<PlanningDurations> = Object.freeze({
  r3ToMockup: 21,
  mockupToR4: 7,
  r4ToFeedback: 7,
  feedbackToIntegration: 14,
});

export const DEFAULT_PLANNING_CAPACITY: Readonly<PlanningCapacity> = Object.freeze({ usual: 2, max: 3 });

/** Lot AE : maquettes par semaine (Réglages > Planning). */
export const DEFAULT_MOCKUPS_PER_WEEK = 1;
/** Lot AE : écart maximum R3 → maquette, en semaines (Réglages > Planning). */
export const DEFAULT_R3_TO_MOCKUP_MAX_WEEKS = 5;

/** Délai minimal entre aujourd'hui et le R3 d'un nouveau projet (maquette : `TODAY_OFF + 7`). */
export const MIN_DAYS_BEFORE_R3 = 7;

/** Phrase de la maquette quand il n'y a aucune note. */
export const NO_CONFLICT_NOTE = 'aucun conflit avec les intégrations ni les congés';

/** Nombre de semaines essayées pour placer l'intégration (garde de la maquette). */
export const MAX_INTEGRATION_TRIES = 40;

/**
 * Note ajoutée (en tête) quand aucune semaine libre n'a été trouvée : l'intégration rendue est alors la
 * semaine qui suit la dernière essayée, non vérifiée.
 */
export const NO_FREE_INTEGRATION_SLOT_NOTE =
  "aucun créneau d'intégration libre dans les 40 semaines : date à confirmer à la main";

/** Nombre de semaines essayées pour placer la maquette (lot AE, même garde que l'intégration). */
export const MAX_MOCKUP_TRIES = 40;

/** Note (en tête) quand aucune semaine de maquette n'a été trouvée (lot AE). */
export const NO_FREE_MOCKUP_SLOT_NOTE =
  'aucune semaine de maquette libre dans les 40 semaines : date à confirmer à la main';

/** Texte affiché par la maquette : notes jointes par « · », ou la phrase « aucun conflit… ». */
export function formatPlanningNotes(notes: readonly string[]): string {
  return notes.length ? notes.join(' · ') : NO_CONFLICT_NOTE;
}

/* ------------------------------------------------------------------ utilitaires */

const WEEKDAYS_SHORT_FR = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'] as const;
const MONTHS_SHORT_FR = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
] as const;

/*
 * Calcul interne en numéros de jour (jours depuis le 01/01/1970, calendrier UTC), comme les décalages de
 * la maquette : les dates `AAAA-MM-JJ` ne sont lues qu'à l'entrée et écrites qu'à la sortie.
 */
const DAY_MS = 86_400_000;

function toDay(date: string): number {
  const { year, month, day } = parseIsoDate(date);
  return Date.UTC(year, month - 1, day) / DAY_MS;
}

function fromDay(n: number): string {
  const d = new Date(n * DAY_MS);
  return formatIsoDate({ year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() });
}

/** 1 = lundi … 7 = dimanche (le 01/01/1970 était un jeudi). */
function weekdayOfDay(n: number): number {
  return ((((n + 3) % 7) + 7) % 7) + 1;
}

function mondayOfDay(n: number): number {
  return n - weekdayOfDay(n) + 1;
}

function isWeekendDay(n: number): boolean {
  return weekdayOfDay(n) >= 6;
}

/** Lundi de la semaine contenant `n`, ou le lundi suivant (maquette : `Math.ceil(o / 7) * 7`). */
function mondayOnOrAfterDay(n: number): number {
  const wd = weekdayOfDay(n);
  return wd === 1 ? n : n + 8 - wd;
}

/** « lun. 28 déc. » (format `shortD` de la maquette). */
function shortDay(n: number): string {
  const { month, day } = parseIsoDate(fromDay(n));
  return `${WEEKDAYS_SHORT_FR[weekdayOfDay(n) - 1]} ${day} ${MONTHS_SHORT_FR[month - 1]}`;
}

/** Fermeture « de fin d'année » : semaine dont le lundi tombe entre le 15 décembre et le 7 janvier. */
function isYearEndWeek(monday: number): boolean {
  const { month, day } = parseIsoDate(fromDay(monday));
  return (month === 12 && day >= 15) || (month === 1 && day <= 7);
}

function nonNegative(n: number): number {
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

interface DayAbsence {
  userId: UserId;
  from: number;
  to: number;
  source: PlanningAbsence;
}

function toDayAbsences(absences: readonly PlanningAbsence[]): DayAbsence[] {
  return absences.map((a) => ({ userId: a.userId, from: toDay(a.from), to: toDay(a.to), source: a }));
}

function isOff(absences: readonly DayAbsence[], who: UserId | null, n: number): boolean {
  return who != null && absences.some((a) => a.userId === who && n >= a.from && n <= a.to);
}

/** Première absence de `who` qui chevauche [start, endExclusive) (maquette : `overlapAbs`). */
function overlapAbsence(
  absences: readonly DayAbsence[],
  who: UserId | null,
  start: number,
  endExclusive: number,
): DayAbsence | null {
  if (who == null) return null;
  return absences.find((a) => a.userId === who && a.from < endExclusive && a.to >= start) ?? null;
}

/** Premier jour ouvré (ni week-end, ni congé de `who`) à partir de `n` (maquette : `workDay`). */
function workDay(absences: readonly DayAbsence[], n: number, who: UserId | null): number {
  let current = n;
  let guard = 0;
  while ((isWeekendDay(current) || isOff(absences, who, current)) && guard++ < 80) current++;
  return current;
}

/* ------------------------------------------------------------------ charge */

function integrationMondayDays(integrationStart: string, integrationWeeks: number): number[] {
  const start = toDay(integrationStart);
  const end = start + 7 * integrationWeeks;
  const mondays: number[] = [];
  for (let monday = mondayOfDay(start); monday < end; monday += 7) mondays.push(monday);
  return mondays;
}

function loadByMondayDay(existing: readonly ExistingIntegration[]): Map<number, number> {
  const counts = new Map<number, number>();
  for (const project of existing) {
    for (const monday of integrationMondayDays(project.integrationStart, project.integrationWeeks)) {
      counts.set(monday, (counts.get(monday) ?? 0) + 1);
    }
  }
  return counts;
}

/** Lundis des semaines calendaires touchées par une intégration (démarrée en milieu de semaine, elle déborde d'une semaine). */
export function integrationMondays(integrationStart: string, integrationWeeks: number): string[] {
  return integrationMondayDays(integrationStart, integrationWeeks).map(fromDay);
}

/**
 * Nombre d'intégrations en cours par semaine (clé : lundi). Comme la maquette, une intégration de 2 semaines
 * qui démarre un mardi compte dans 3 semaines calendaires.
 * Sans `range`, seules les semaines chargées figurent ; avec `range`, toutes les semaines dont le lundi est
 * entre `mondayOf(range.from)` et `mondayOf(range.to)` figurent (0 compris), et seulement elles.
 */
export function weekLoad(
  existing: readonly ExistingIntegration[],
  range?: { from: string; to: string },
): WeekLoad {
  const counts = loadByMondayDay(existing);
  const load: WeekLoad = {};
  if (range) {
    const last = mondayOfDay(toDay(range.to));
    for (let monday = mondayOfDay(toDay(range.from)); monday <= last; monday += 7) {
      load[fromDay(monday)] = counts.get(monday) ?? 0;
    }
    return load;
  }
  for (const monday of [...counts.keys()].sort((a, b) => a - b))
    load[fromDay(monday)] = counts.get(monday) ?? 0;
  return load;
}

/** Maquettes qui démarrent chaque semaine (clé : lundi), maquettes sous-traitées écartées (lot AE). */
function mockupsByMondayDay(existing: readonly ExistingMockup[] | undefined): Map<number, number> {
  const counts = new Map<number, number>();
  for (const mockup of existing ?? []) {
    if (mockup.subcontracted) continue;
    const monday = mondayOfDay(toDay(mockup.mockupStart));
    counts.set(monday, (counts.get(monday) ?? 0) + 1);
  }
  return counts;
}

/**
 * Lot AE : nombre de maquettes qui démarrent chaque semaine (clé : lundi), hors sous-traitance (ligne « Maquettes /
 * semaine » du Gantt). Mêmes règles de période que `weekLoad`.
 */
export function weekMockups(
  existing: readonly ExistingMockup[],
  range?: { from: string; to: string },
): WeekLoad {
  const counts = mockupsByMondayDay(existing);
  const load: WeekLoad = {};
  if (range) {
    const last = mondayOfDay(toDay(range.to));
    for (let monday = mondayOfDay(toDay(range.from)); monday <= last; monday += 7) {
      load[fromDay(monday)] = counts.get(monday) ?? 0;
    }
    return load;
  }
  for (const monday of [...counts.keys()].sort((a, b) => a - b))
    load[fromDay(monday)] = counts.get(monday) ?? 0;
  return load;
}

/* ------------------------------------------------------------------ simulation */

/**
 * Date de mise en ligne d'un nouveau projet et ses étapes (CDC §3.6, revu au lot AE : la maquette d'abord).
 *
 * - R3 au plus tôt : `today + 7 j` (ou `startNotBefore` s'il est plus tard), sur un jour ouvré hors congés de la
 *   personne des rendez-vous ;
 * - maquette (lot AE) : la première semaine (elle commence le lundi) à partir de « R3 au plus tôt + délai minimum »
 *   (`r3ToMockup`, + 7 j en sous-traitance) qui n'est pas fermée, où moins de `mockupsPerWeek` maquettes démarrent
 *   déjà (projets existants, hors sous-traitance) et sans congé de la maquettiste pendant la maquette (ces deux
 *   derniers points ne comptent pas en sous-traitance) ;
 * - R3 : calé au délai minimum avant la maquette, jour ouvré hors congés de la personne des rendez-vous ; s'il le
 *   faut plus tôt, jamais plus de `r3ToMockupMaxWeeks` semaines avant la maquette ni avant le R3 au plus tôt
 *   (sinon la semaine de maquette suivante est essayée) ;
 * - R4 : `maquette + mockupToR4`, jour ouvré hors congés de la personne des rendez-vous ;
 * - retours : `R4 + r4ToFeedback` ;
 * - intégration : lundi au plus tôt après `retours + feedbackToIntegration`, puis semaine par semaine
 *   jusqu'à ce que chacune de ses semaines ait une charge < `capacity.usual`, ne soit pas fermée et ne
 *   chevauche pas (du lundi au vendredi) un congé de la personne de l'intégration ;
 * - R5 : fin de l'intégration (lundi suivant), repoussée hors semaine fermée, congé de la personne des
 *   rendez-vous et week-end.
 */
export function simulate(input: SimulateInput, options: SimulateOptions = {}): SimulateResult {
  const c = chainContext(input, options);
  const minStart = toDay(input.today) + MIN_DAYS_BEFORE_R3;
  const start = input.startNotBefore ? Math.max(minStart, toDay(input.startNotBefore)) : minStart;

  if (isOff(c.absences, c.meetings, start)) c.notes.push(`R3 après les congés de ${c.name(c.meetings)}`);
  const { r3, mockup, mockupDelayedWeeks } = placeMockupWeek(c, start);
  const r4 = placeR4(c, mockup);
  const feedback = r4 + c.d3;
  const placed = placeIntegration(c, feedback);
  const r5 = placeR5(c, placed.integration);
  finishNotes(c, placed);

  return {
    r3: fromDay(r3),
    mockup: fromDay(mockup),
    r4: fromDay(r4),
    feedback: fromDay(feedback),
    integration: fromDay(placed.integration),
    r5: fromDay(r5),
    notes: c.notes,
    delayedWeeks: placed.delayedWeeks,
    mockupDelayedWeeks,
  };
}

/* Étapes du calcul, partagées par `simulate` et `replanFrom` (lot K). */

interface ChainContext {
  d1: number;
  d2: number;
  d3: number;
  d4: number;
  capacity: number;
  integrationWeeks: number;
  subcontracted: boolean;
  absences: DayAbsence[];
  closed: Set<number>;
  load: Map<number, number>;
  name: (who: UserId) => string;
  notes: string[];
  meetings: UserId;
  mockupBy: UserId | null;
  integrationBy: UserId;
  /** Lot AE : maquettes par semaine, écart maximum R3 → maquette (semaines), maquettes par lundi. */
  mockupsPerWeek: number;
  r3MaxWeeks: number;
  mockupLoad: Map<number, number>;
}

/**
 * Entrée commune aux calculs. Les règles des maquettes (lot AE) sont facultatives pour `replanFrom` et
 * `resizeProject` : sans elles, 1 maquette par semaine, 5 semaines au plus, aucune maquette existante.
 */
type ChainInput = Pick<
  SimulateInput,
  'subcontracted' | 'durations' | 'capacity' | 'roles' | 'absences' | 'closedWeeks' | 'existing'
> &
  Partial<Pick<SimulateInput, 'mockupsPerWeek' | 'r3ToMockupMaxWeeks' | 'existingMockups'>> & {
    integrationWeeks: number;
  };

function chainContext(input: ChainInput, options: SimulateOptions): ChainContext {
  return {
    d1: nonNegative(input.durations.r3ToMockup),
    d2: nonNegative(input.durations.mockupToR4),
    d3: nonNegative(input.durations.r4ToFeedback),
    d4: nonNegative(input.durations.feedbackToIntegration),
    capacity: Math.max(1, input.capacity.usual),
    integrationWeeks: Math.max(1, Math.round(input.integrationWeeks)),
    subcontracted: input.subcontracted,
    absences: toDayAbsences(input.absences),
    closed: new Set(input.closedWeeks.map((w) => mondayOfDay(toDay(w)))),
    load: loadByMondayDay(input.existing),
    name: (who) => options.names?.[who] ?? who,
    notes: [],
    meetings: input.roles.meetings,
    mockupBy: input.subcontracted ? null : input.roles.mockup,
    integrationBy: input.roles.integration,
    mockupsPerWeek: Math.max(1, Math.round(input.mockupsPerWeek ?? DEFAULT_MOCKUPS_PER_WEEK)),
    r3MaxWeeks: Math.max(1, Math.round(input.r3ToMockupMaxWeeks ?? DEFAULT_R3_TO_MOCKUP_MAX_WEEKS)),
    mockupLoad: mockupsByMondayDay(input.existingMockups),
  };
}

/**
 * Maquette `R3 + r3ToMockup` (+ 7 j en sous-traitance) hors congés de la maquettiste, puis R4 : recalcul à partir
 * d'un R3 fixé (`replanFrom`, lot K). Lot AE : la maquette respecte la limite de maquettes par semaine et les
 * semaines fermées (`placeMockupFrom`, comme un changement de durée).
 */
function placeMockupAndR4(c: ChainContext, r3: number): { mockup: number; r4: number } {
  const mockup = placeMockupFrom(c, r3 + c.d1 + (c.subcontracted ? 7 : 0), c.d2);
  return { mockup, r4: placeR4(c, mockup) };
}

/** R4 : `maquette + mockupToR4`, jour ouvré hors congés de la personne des rendez-vous. */
function placeR4(c: ChainContext, mockup: number): number {
  const r4Raw = mockup + c.d2;
  const r4 = workDay(c.absences, r4Raw, c.meetings);
  if (isOff(c.absences, c.meetings, r4Raw)) c.notes.push(`R4 décalé, ${c.name(c.meetings)} en congés`);
  return r4;
}

/** « 9 », « 1er » : jour du mois tel qu'on l'écrit. */
function dayNumber(n: number): string {
  const { day } = parseIsoDate(fromDay(n));
  return day === 1 ? '1er' : String(day);
}

/** « 14 oct. », « 1er déc. » (lot AL). */
function dayMonth(n: number): string {
  const { month } = parseIsoDate(fromDay(n));
  return `${dayNumber(n)} ${MONTHS_SHORT_FR[month - 1]}`;
}

/** « 9, 16 et 23 nov. », « 30 nov. et 7 déc. » : lundis groupés par mois. */
function mondayList(mondays: readonly number[]): string {
  const tokens = mondays.map((monday, i) => {
    const { month } = parseIsoDate(fromDay(monday));
    const next = mondays[i + 1];
    const lastOfMonth = next === undefined || parseIsoDate(fromDay(next)).month !== month;
    return lastOfMonth ? `${dayNumber(monday)} ${MONTHS_SHORT_FR[month - 1]}` : dayNumber(monday);
  });
  if (tokens.length <= 1) return tokens.join('');
  return `${tokens.slice(0, -1).join(', ')} et ${tokens.at(-1)}`;
}

/** « les semaines du 9, 16 et 23 nov. ont déjà leur maquette » (lot AE). */
function fullWeeksNote(mondays: readonly number[], perWeek: number): string {
  const what = perWeek > 1 ? `${perWeek} maquettes` : 'maquette';
  return mondays.length > 1
    ? `les semaines du ${mondayList(mondays)} ont déjà ${perWeek > 1 ? 'leurs' : 'leur'} ${what}`
    : `la semaine du ${mondayList(mondays)} a déjà ${perWeek > 1 ? 'ses' : 'sa'} ${what}`;
}

/** « 3 semaines », « 10 jours ». */
function gapLabel(days: number): string {
  if (days % 7 === 0) return `${days / 7} semaine${days / 7 > 1 ? 's' : ''}`;
  return `${days} jour${days > 1 ? 's' : ''}`;
}

/** Raison pour laquelle aucune maquette ne peut démarrer la semaine du lundi `monday` (null : place libre). */
function mockupWeekBlocked(
  c: ChainContext,
  monday: number,
): { kind: 'closed' | 'full'; text: string } | null {
  if (c.closed.has(monday)) {
    return {
      kind: 'closed',
      text: isYearEndWeek(monday)
        ? "maquette après la fermeture de fin d'année"
        : `maquette après la fermeture de l'agence (semaine du ${shortDay(monday)})`,
    };
  }
  if (!c.subcontracted && (c.mockupLoad.get(monday) ?? 0) >= c.mockupsPerWeek) {
    return { kind: 'full', text: fullWeeksNote([monday], c.mockupsPerWeek) };
  }
  return null;
}

/** Dernier jour ouvré de la personne des rendez-vous entre `lower` et `ideal` (inclus), en reculant ; null : aucun. */
function r3Before(c: ChainContext, ideal: number, lower: number): number | null {
  for (let day = ideal; day >= lower; day--) {
    if (!isWeekendDay(day) && !isOff(c.absences, c.meetings, day)) return day;
  }
  return null;
}

/**
 * Lot AE : semaine de la maquette d'un nouveau projet, puis son R3 (voir `simulate`). `start` = R3 au plus tôt
 * (avant le jour ouvré). Sans semaine libre dans les 40 essayées : la suivante, avec une note à confirmer.
 */
function placeMockupWeek(
  c: ChainContext,
  start: number,
): { r3: number; mockup: number; mockupDelayedWeeks: number } {
  const r3Earliest = workDay(c.absences, start, c.meetings);
  const minGap = c.d1 + (c.subcontracted ? 7 : 0);
  const maxGap = Math.max(minGap, 7 * c.r3MaxWeeks);
  const mockupLength = Math.max(5, c.d2);
  const first = mondayOnOrAfterDay(r3Earliest + minGap);

  const full: number[] = [];
  let closedText: string | null = null;
  const absentUsers: UserId[] = [];
  let r3Missing = false;
  let found: { r3: number; mockup: number } | null = null;
  for (let tries = 0; tries < MAX_MOCKUP_TRIES && !found; tries++) {
    const monday = first + 7 * tries;
    const blocked = mockupWeekBlocked(c, monday);
    if (blocked) {
      if (blocked.kind === 'full') full.push(monday);
      else closedText ??= blocked.text;
      continue;
    }
    const absence = overlapAbsence(c.absences, c.mockupBy, monday, monday + mockupLength);
    if (absence) {
      if (!absentUsers.includes(absence.userId)) absentUsers.push(absence.userId);
      continue;
    }
    const r3 = r3Before(c, monday - minGap, Math.max(start, monday - maxGap));
    if (r3 === null) {
      r3Missing = true;
      continue;
    }
    found = { r3, mockup: monday };
  }

  if (!found) {
    const mockup = first + 7 * MAX_MOCKUP_TRIES;
    c.notes.unshift(NO_FREE_MOCKUP_SLOT_NOTE);
    found = { mockup, r3: workDay(c.absences, Math.max(start, mockup - minGap), c.meetings) };
  }
  const { r3, mockup } = found;
  const ideal = mockup - minGap;
  if (r3 < ideal) {
    c.notes.push(`R3 avancé au ${shortDay(r3)}, ${c.name(c.meetings)} en congés le ${shortDay(ideal)}`);
  } else if (mockup > first) {
    c.notes.push(
      `R3 calé ${gapLabel(mockup - r3)} avant la maquette (au lieu du ${shortDay(r3Earliest)} au plus tôt)`,
    );
  }
  const before = (monday: number) => monday < mockup;
  const fullBefore = full.filter(before);
  if (fullBefore.length > 0) c.notes.push(fullWeeksNote(fullBefore, c.mockupsPerWeek));
  if (closedText && mockup > first) c.notes.push(closedText);
  for (const who of absentUsers) c.notes.push(`maquette après les congés de ${c.name(who)}`);
  if (r3Missing) {
    c.notes.push(`maquette décalée : pas de R3 possible avant, ${c.name(c.meetings)} en congés`);
  }
  return { r3, mockup, mockupDelayedWeeks: (mockup - first) / 7 };
}

interface PlacedIntegration {
  integration: number;
  earliest: number;
  blocked: string | null;
  /** Lot BJ : première semaine refusée (même raison que `blocked`, avec sa nature et son lundi). */
  block: IntegrationWeekBlock | null;
  slotFound: boolean;
  delayedWeeks: number;
}

/**
 * Lot BJ : pourquoi une semaine ne peut pas recevoir l'intégration : semaine fermée, capacité pleine, congés de la
 * personne de l'intégration. `text` est la phrase des notes du planning (« 2 intégrations déjà prévues la semaine
 * du lun. 26 oct. »), `short` l'étiquette courte du Gantt (« semaine du 26 oct. pleine »).
 */
export interface IntegrationWeekBlock {
  kind: 'closed' | 'full' | 'absence';
  /** Lundi de la semaine refusée. */
  monday: string;
  text: string;
  short: string;
}

/** Intégration : lundi au plus tôt après `retours + feedbackToIntegration`, puis la première semaine libre. */
function placeIntegration(c: ChainContext, feedback: number): PlacedIntegration {
  return placeIntegrationFrom(c, mondayOnOrAfterDay(feedback + c.d4));
}

/**
 * Raison pour laquelle l'intégration ne peut pas occuper la semaine du lundi `monday` (null : libre). Sans
 * `absences`, les congés de la personne de l'intégration ne sont pas regardés.
 */
function weekBlock(c: ChainContext, monday: number, absences = true): IntegrationWeekBlock | null {
  const week = `semaine du ${dayMonth(monday)}`;
  if (c.closed.has(monday)) {
    return {
      kind: 'closed',
      monday: fromDay(monday),
      text: isYearEndWeek(monday)
        ? "fermeture de fin d'année"
        : `fermeture de l'agence la semaine du ${shortDay(monday)}`,
      short: `${week} fermée`,
    };
  }
  if ((c.load.get(monday) ?? 0) >= c.capacity) {
    return {
      kind: 'full',
      monday: fromDay(monday),
      text: `${c.capacity} intégrations déjà prévues la semaine du ${shortDay(monday)}`,
      short: `${week} pleine`,
    };
  }
  if (absences && overlapAbsence(c.absences, c.integrationBy, monday, monday + 5)) {
    return {
      kind: 'absence',
      monday: fromDay(monday),
      text: `${c.name(c.integrationBy)} en congés la semaine du ${shortDay(monday)}`,
      short: `${c.name(c.integrationBy)} en congés la ${week}`,
    };
  }
  return null;
}

/**
 * Lot BP : une semaine où une intégration déplacée à la main dépasse la capacité (ou une maquette la limite de
 * maquettes, `mockupOverloads`). Une alerte, jamais un refus (« ça alerte avec le message rouge, mais ça accepte le
 * déplacement », Maxime, 02/11/2026).
 */
export interface IntegrationOverload {
  /** Lundi de la semaine. */
  monday: string;
  /** Intégrations (ou maquettes) de la semaine, ce projet compris. */
  count: number;
  usual: number;
  max: number;
  /** Au-dessus de la capacité maximale des Réglages. */
  beyondMax: boolean;
  /** « 3 intégrations la semaine du lun. 26 oct. : au-dessus du rythme habituel (2) » */
  text: string;
}

/**
 * Lot BP : semaines au-dessus de la capacité habituelle pour une intégration du lundi `start` sur `weeks` semaines
 * (`existing` ne doit pas contenir ce projet). Les semaines où le projet était déjà (`before`) ne sont pas signalées :
 * ce déplacement ne les charge pas davantage.
 */
export function integrationOverloads(input: {
  start: string | null | undefined;
  weeks: number;
  existing: readonly ExistingIntegration[];
  capacity: PlanningCapacity;
  before?: { start: string | null | undefined; weeks: number };
}): IntegrationOverload[] {
  if (!input.start) return [];
  const usual = Math.max(1, input.capacity.usual);
  const max = Math.max(usual, input.capacity.max);
  const load = loadByMondayDay(input.existing);
  const already = new Set(
    input.before?.start ? integrationMondays(input.before.start, input.before.weeks) : [],
  );
  return integrationMondays(input.start, input.weeks).flatMap((monday) => {
    if (already.has(monday)) return [];
    const day = toDay(monday);
    const count = (load.get(day) ?? 0) + 1;
    if (count <= usual) return [];
    const beyondMax = count > max;
    const limit = beyondMax ? `du maximum (${max})` : `du rythme habituel (${usual})`;
    return [
      {
        monday,
        count,
        usual,
        max,
        beyondMax,
        text: `${count} intégrations la semaine du ${shortDay(day)} : au-dessus ${limit}`,
      },
    ];
  });
}

/**
 * Semaines où une maquette déplacée à la main dépasse la limite de maquettes par semaine : même règle que les
 * intégrations (« même fonctionnement », Maxime, 02/11/2026), une alerte, jamais un report. `existing` ne doit pas
 * contenir ce projet. Rien pour une maquette sous-traitée ni si elle reste dans sa semaine (`before`).
 */
export function mockupOverloads(input: {
  start: string | null | undefined;
  subcontracted?: boolean;
  existing: readonly ExistingMockup[];
  perWeek: number;
  before?: string | null;
}): IntegrationOverload[] {
  if (!input.start || input.subcontracted) return [];
  const day = mondayOfDay(toDay(input.start));
  if (input.before && mondayOfDay(toDay(input.before)) === day) return [];
  const limit = Math.max(1, Math.round(input.perWeek));
  const count = (mockupsByMondayDay(input.existing).get(day) ?? 0) + 1;
  if (count <= limit) return [];
  return [
    {
      monday: fromDay(day),
      count,
      usual: limit,
      max: limit,
      beyondMax: true,
      text: `${count} maquettes la semaine du ${shortDay(day)} : au-dessus de la limite (${limit})`,
    },
  ];
}

/** Intégration à partir du lundi `earliest`, semaine par semaine jusqu'à la première place libre. */
function placeIntegrationFrom(c: ChainContext, earliest: number): PlacedIntegration {
  let integration = earliest;
  let block: IntegrationWeekBlock | null = null;
  let slotFound = false;
  for (let tries = 0; tries < MAX_INTEGRATION_TRIES; tries++) {
    let why: IntegrationWeekBlock | null = null;
    for (let k = 0; k < c.integrationWeeks; k++) {
      why = weekBlock(c, integration + 7 * k);
      if (why) break;
    }
    if (!why) {
      slotFound = true;
      break;
    }
    if (block == null) block = why;
    integration += 7;
  }
  return {
    integration,
    earliest,
    blocked: block?.text ?? null,
    block,
    slotFound,
    delayedWeeks: (integration - earliest) / 7,
  };
}

/** R5 : fin de l'intégration, repoussée hors semaine fermée, congé de la personne des rendez-vous et week-end. */
function placeR5(c: ChainContext, integration: number): number {
  return r5From(c, integration + 7 * c.integrationWeeks);
}

/** Premier jour à partir de `start` hors semaine fermée, congé de la personne des rendez-vous et week-end. */
function r5From(c: ChainContext, start: number): number {
  let r5 = start;
  let guard = 0;
  while (
    (c.closed.has(mondayOfDay(r5)) || isOff(c.absences, c.meetings, r5) || isWeekendDay(r5)) &&
    guard++ < 60
  ) {
    r5++;
  }
  return r5;
}

/** Notes de tête : décalage de l'intégration, puis absence de créneau libre (ordre de la maquette). */
function finishNotes(c: ChainContext, placed: PlacedIntegration): void {
  if (placed.delayedWeeks > 0) {
    const n = placed.delayedWeeks;
    c.notes.unshift(`intégration décalée de ${n} semaine${n > 1 ? 's' : ''} : ${placed.blocked}`);
  }
  if (!placed.slotFound) c.notes.unshift(NO_FREE_INTEGRATION_SLOT_NOTE);
}

/** Alternative « en acceptant `capacity.max` intégrations » : la même simulation avec la capacité maximale. */
export function simulateAtMaxCapacity(input: SimulateInput, options: SimulateOptions = {}): SimulateResult {
  const max = Math.max(input.capacity.usual, input.capacity.max);
  return simulate({ ...input, capacity: { usual: max, max } }, options);
}

/* ------------------------------------------------------------------ projets partenaires (lot AL) */

/** Lot AL : délai entre la signature et l'intégration au plus tôt d'un projet partenaire (Réglages > Planning). */
export const DEFAULT_PARTNER_INTEGRATION_DELAY_DAYS = 7;

/**
 * Entrée de `simulatePartner` (lot AL) : un projet partenaire n'a que l'intégration puis le R5 (l'amont est géré
 * par le partenaire). Pas de maquette : les maquettes existantes et leur limite ne comptent pas.
 */
export interface PartnerSimulateInput extends Pick<
  SimulateInput,
  'today' | 'startNotBefore' | 'capacity' | 'roles' | 'absences' | 'closedWeeks' | 'existing'
> {
  /** Date de signature du devis (null : inconnue, on part d'aujourd'hui). */
  signedOn: string | null;
  /** `partnerIntegrationDelayDays` : jours après la signature avant l'intégration au plus tôt. */
  delayDays: number;
  integrationWeeks: number;
}

export interface PartnerSimulateResult {
  /** Jour au plus tôt (signature + délai, jamais avant aujourd'hui ni `startNotBefore`). */
  earliest: string;
  /** Lundi de début de l'intégration. */
  integration: string;
  /** R5 · rendu du site (fin de l'intégration, comme nos projets). */
  r5: string;
  /** Explications (première note : d'où vient la date au plus tôt). */
  notes: string[];
  /** Semaines de décalage de l'intégration par rapport au lundi au plus tôt. */
  delayedWeeks: number;
}

/**
 * Planning d'un projet partenaire (lot AL, décision de Maxime du 14/10/2026) : intégration au plus tôt =
 * signature du devis + `delayDays` (sans signature connue : aujourd'hui), jamais avant aujourd'hui ni
 * `startNotBefore` ; puis le premier lundi dont chaque semaine d'intégration est sous la capacité habituelle
 * (intégrations de tous les projets, partenaires compris), n'est pas fermée et ne chevauche pas un congé de la
 * personne de l'intégration ; R5 = fin de l'intégration, hors semaine fermée, congé de la personne des
 * rendez-vous et week-end (comme `simulate`).
 */
export function simulatePartner(
  input: PartnerSimulateInput,
  options: SimulateOptions = {},
): PartnerSimulateResult {
  const c = chainContext(
    {
      ...input,
      subcontracted: true,
      durations: DEFAULT_PLANNING_DURATIONS,
      integrationWeeks: input.integrationWeeks,
    },
    options,
  );
  const delay = Math.round(nonNegative(input.delayDays));
  const today = toDay(input.today);
  const fromSignature = input.signedOn ? toDay(input.signedOn) + delay : today;
  const notBefore = input.startNotBefore ? toDay(input.startNotBefore) : today;
  const earliest = Math.max(fromSignature, today, notBefore);
  const placed = placeIntegrationFrom(c, mondayOnOrAfterDay(earliest));
  const r5 = placeR5(c, placed.integration);
  finishNotes(c, placed);
  const days = `${delay} jour${delay > 1 ? 's' : ''}`;
  let origin: string;
  if (input.startNotBefore && notBefore >= fromSignature && notBefore > today) {
    origin = `démarrage demandé à partir du ${dayMonth(notBefore)}`;
  } else if (input.signedOn && fromSignature >= today) {
    origin = `signé le ${dayMonth(toDay(input.signedOn))} + ${days}`;
  } else if (input.signedOn) {
    origin = `signé le ${dayMonth(toDay(input.signedOn))} + ${days} (déjà passé) : au plus tôt dès aujourd'hui`;
  } else {
    origin = "signature inconnue : au plus tôt dès aujourd'hui";
  }
  const placement =
    placed.delayedWeeks === 0
      ? `première semaine sous la limite (${c.capacity} intégration${c.capacity > 1 ? 's' : ''})`
      : null;
  return {
    earliest: fromDay(earliest),
    integration: fromDay(placed.integration),
    r5: fromDay(r5),
    notes: [placement ? `${origin}, ${placement}` : origin, ...c.notes],
    delayedWeeks: placed.delayedWeeks,
  };
}

/** Alternative « en acceptant `capacity.max` intégrations » pour un projet partenaire (lot AL). */
export function simulatePartnerAtMaxCapacity(
  input: PartnerSimulateInput,
  options: SimulateOptions = {},
): PartnerSimulateResult {
  const max = Math.max(input.capacity.usual, input.capacity.max);
  return simulatePartner({ ...input, capacity: { usual: max, max } }, options);
}

/* ------------------------------------------------------------------ conflits de congés */

/** Clés des phases, dans l'ordre (mêmes clés que `projects.phases`, CDC §3.2). */
export const PHASE_KEYS = ['R3', 'Maquette', 'Ajustements', 'R4', 'Retours', 'Integration', 'R5'] as const;
export type PhaseKey = (typeof PHASE_KEYS)[number];

const MEETING_PHASES: ReadonlySet<PhaseKey> = new Set(['R3', 'R4', 'R5']);

export interface PlanningProject {
  /** Date de début de chaque phase (`null` ou absente : pas de phase). */
  phases: Partial<Record<PhaseKey, string | null>>;
  integrationWeeks: number;
  /** Maquette sous-traitée : la personne des maquettes n'est pas concernée par Maquette/Ajustements. */
  subcontracted?: boolean;
}

export interface PhaseSegment {
  phase: PhaseKey;
  kind: 'meeting' | 'bar';
  /** Premier jour (inclus). */
  from: string;
  /** Dernier jour (inclus). */
  to: string;
}

export interface PhaseConflict extends PhaseSegment {
  /** Personne responsable de la phase. */
  userId: UserId;
  /** Premier congé de cette personne qui chevauche la phase. */
  absence: PlanningAbsence;
}

/**
 * Segments d'un projet (maquette : `projSegments`) : un rendez-vous dure un jour ; l'intégration dure
 * `integrationWeeks` semaines ; une autre phase dure jusqu'à la veille de la phase suivante qui commence
 * après elle, ou 7 jours s'il n'y en a pas.
 */
export function projectSegments(project: PlanningProject): PhaseSegment[] {
  const { phases } = project;
  const segments: PhaseSegment[] = [];
  PHASE_KEYS.forEach((phase, index) => {
    const start = phases[phase];
    if (start == null) return;
    if (MEETING_PHASES.has(phase)) {
      segments.push({ phase, kind: 'meeting', from: start, to: start });
      return;
    }
    let endExclusive: string;
    if (phase === 'Integration') {
      endExclusive = addDays(start, 7 * project.integrationWeeks);
    } else {
      const next = PHASE_KEYS.slice(index + 1)
        .map((key) => phases[key])
        .find((value): value is string => value != null && value > start);
      endExclusive = next ?? addDays(start, 7);
    }
    segments.push({ phase, kind: 'bar', from: start, to: addDays(endExclusive, -1) });
  });
  return segments;
}

/** Responsable d'une phase (maquette : `phaseWho`) ; les retours n'ont pas de responsable. */
export function phaseOwner(phase: PhaseKey, roles: PlanningRoles): UserId | null {
  if (MEETING_PHASES.has(phase)) return roles.meetings;
  if (phase === 'Maquette' || phase === 'Ajustements') return roles.mockup;
  if (phase === 'Integration') return roles.integration;
  return null;
}

/**
 * Phases d'un projet qui chevauchent un congé de leur responsable (contour rouge de la maquette) :
 * R3/R4/R5 → rendez-vous, Maquette/Ajustements → maquettes, Intégration → intégration.
 */
export function conflicts(
  project: PlanningProject,
  roles: PlanningRoles,
  absences: readonly PlanningAbsence[],
): PhaseConflict[] {
  const dayAbsences = toDayAbsences(absences);
  const found: PhaseConflict[] = [];
  for (const segment of projectSegments(project)) {
    if (project.subcontracted && (segment.phase === 'Maquette' || segment.phase === 'Ajustements')) continue;
    const userId = phaseOwner(segment.phase, roles);
    if (userId == null) continue;
    const absence = overlapAbsence(dayAbsences, userId, toDay(segment.from), toDay(segment.to) + 1);
    if (absence) found.push({ ...segment, userId, absence: absence.source });
  }
  return found;
}

/* ------------------------------------------------------------------ périodes de rendez-vous (lot K) */

/** Rendez-vous calés avec le client (mêmes clés que `MEETING_PHASE_KEYS` de `projects.ts`). */
export type MeetingKey = 'R3' | 'R4' | 'R5';

/** Longueur par défaut de la période pour fixer un rendez-vous (Réglages > Planning). */
export const DEFAULT_MEETING_WINDOW_DAYS = 14;
/** Durée d'intégration proposée par défaut (Réglages > Planning). */
export const DEFAULT_INTEGRATION_WEEKS = 2;

export interface MeetingWindow {
  /** Premier jour de la période : la date que le planning aurait choisie pour le rendez-vous. */
  windowStart: string;
  /** Dernier jour de la période : `windowStart + meetingWindowDays` (« 20 oct. → 3 nov. » pour 14 jours). */
  windowEnd: string;
}

/**
 * Périodes de rendez-vous d'un projet préparé (lot K) : chaque R3, R4 et R5 commence à la date choisie par la
 * simulation et dure `days` jours. Aucun rendez-vous n'est posé dans l'agenda à la préparation.
 */
export function meetingWindows(
  dates: Pick<SimulateResult, 'r3' | 'r4' | 'r5'>,
  days: number,
): Record<MeetingKey, MeetingWindow> {
  const length = Math.max(1, Math.round(nonNegative(days)));
  const window = (start: string): MeetingWindow => ({
    windowStart: start,
    windowEnd: addDays(start, length),
  });
  return { R3: window(dates.r3), R4: window(dates.r4), R5: window(dates.r5) };
}

/** Premier jour ouvré (ni week-end, ni congé de `who`) à partir de `from`. */
export function firstWorkingDay(
  from: string,
  absences: readonly PlanningAbsence[],
  who: UserId | null,
): string {
  return fromDay(workDay(toDayAbsences(absences), toDay(from), who));
}

/**
 * Date proposée pour fixer un rendez-vous dans sa période : le premier jour ouvré de la période (hors congés de
 * la personne des rendez-vous) ; si la période a déjà commencé, le premier jour ouvré à partir de demain.
 */
export function defaultMeetingDate(
  window: Pick<MeetingWindow, 'windowStart'>,
  today: string,
  absences: readonly PlanningAbsence[],
  who: UserId | null,
): string {
  const from = window.windowStart > today ? window.windowStart : addDays(today, 1);
  return firstWorkingDay(from, absences, who);
}

export interface ReplanInput extends ChainInput {
  /** Dates actuelles du projet (clés de `projects.phases`). */
  phases: Partial<Record<PhaseKey, string | null>>;
  /** Le rendez-vous qui vient d'être fixé, et sa date réelle. */
  meeting: MeetingKey;
  date: string;
  /** Rendez-vous suivants déjà fixés : leur date ne bouge pas et le calcul repart d'eux. */
  fixed?: readonly MeetingKey[];
}

export interface ReplanResult {
  /** Nouvelles dates (objet complet, `null` = pas de phase). */
  phases: Record<PhaseKey, string | null>;
  /** Explications (congés, intégration décalée…), comme la simulation. */
  notes: string[];
  delayedWeeks: number;
}

/**
 * Recalcul des jalons suivants à partir de la date réelle d'un rendez-vous (lot K), avec les mêmes durées,
 * capacité, congés et semaines de fermeture que la simulation :
 * - R3 fixé : maquette (lot AE : dans la première semaine sous la limite de maquettes, hors semaine fermée), R4,
 *   retours, intégration, R5 ;
 * - R4 fixé : retours, intégration, R5 ;
 * - R5 fixé : rien d'autre ne bouge.
 * Un rendez-vous suivant déjà fixé (`fixed`) garde sa date et le calcul repart de lui. Une phase absente du
 * projet (ex. pas de R4) reste absente, mais le calcul passe par sa date théorique. `existing` et
 * `existingMockups` ne doivent pas contenir l'intégration ni la maquette du projet lui-même.
 */
export function replanFrom(input: ReplanInput, options: SimulateOptions = {}): ReplanResult {
  const c = chainContext(input, options);
  const fixed = new Set(input.fixed ?? []);
  const out = Object.fromEntries(PHASE_KEYS.map((key) => [key, input.phases[key] ?? null])) as Record<
    PhaseKey,
    string | null
  >;
  const has = (key: PhaseKey) => input.phases[key] != null;
  out[input.meeting] = input.date;
  if (input.meeting === 'R5') return { phases: out, notes: c.notes, delayedWeeks: 0 };

  let r4: number;
  if (input.meeting === 'R3') {
    const placed = placeMockupAndR4(c, toDay(input.date));
    const oldMockup = input.phases.Maquette;
    if (oldMockup && input.phases.Ajustements) {
      out.Ajustements = addDays(input.phases.Ajustements, placed.mockup - toDay(oldMockup));
    }
    if (has('Maquette')) out.Maquette = fromDay(placed.mockup);
    if (fixed.has('R4') && input.phases.R4) {
      r4 = toDay(input.phases.R4);
    } else {
      r4 = placed.r4;
      if (has('R4')) out.R4 = fromDay(r4);
    }
  } else {
    r4 = toDay(input.date);
  }

  const feedback = r4 + c.d3;
  if (has('Retours')) out.Retours = fromDay(feedback);
  const placed = placeIntegration(c, feedback);
  if (has('Integration')) out.Integration = fromDay(placed.integration);
  if (has('R5') && !fixed.has('R5')) out.R5 = fromDay(placeR5(c, placed.integration));
  finishNotes(c, placed);
  return { phases: out, notes: c.notes, delayedWeeks: placed.delayedWeeks };
}

/* ------------------------------------------------------------------ durées d'un projet (lot O) */

/**
 * Phases dont la longueur se règle projet par projet (lot O, décision de Maxime du 13/10/2026) : la longueur
 * d'une phase va de son début au début de la phase suivante du projet (dans l'ordre R3 → R5, phases sans date
 * ignorées). L'intégration se règle à part, en semaines (`projects.integration_weeks`).
 * - `Maquette` : création de la maquette (`mockupToR4` quand le R4 suit) ;
 * - `R4` : retours du client (`r4ToFeedback`) ;
 * - `Retours` : prise en compte des retours (`feedbackToIntegration`) ;
 * - `R3` et `Ajustements` : pour les projets qui en ont (import de « Timeline projets »).
 */
export const LENGTH_PHASE_KEYS = ['R3', 'Maquette', 'Ajustements', 'R4', 'Retours'] as const;
export type LengthPhaseKey = (typeof LENGTH_PHASE_KEYS)[number];
export type PhaseLengths = Partial<Record<LengthPhaseKey, number>>;

/** Durées propres à un projet (`projects.durations`, lot O) : elles remplacent celles des Réglages pour lui. */
export type ProjectDurationOverrides = Partial<PlanningDurations>;

/** Plus longue intégration acceptée (contrainte de `projects.integration_weeks`). */
export const MAX_INTEGRATION_WEEKS = 12;

const PHASE_WORDS: Record<PhaseKey, string> = {
  R3: 'R3',
  Maquette: 'maquette',
  Ajustements: 'ajustements',
  R4: 'R4',
  Retours: 'retours',
  Integration: 'intégration',
  R5: 'mise en ligne (R5)',
};

function fullPhases(phases: Partial<Record<PhaseKey, string | null>>): Record<PhaseKey, string | null> {
  return Object.fromEntries(PHASE_KEYS.map((key) => [key, phases[key] ?? null])) as Record<
    PhaseKey,
    string | null
  >;
}

/** Phase datée qui suit `key` dans l'ordre R3 → R5 (null : aucune). */
export function nextDatedPhase(
  phases: Partial<Record<PhaseKey, string | null>>,
  key: PhaseKey,
): PhaseKey | null {
  return PHASE_KEYS.slice(PHASE_KEYS.indexOf(key) + 1).find((k) => phases[k] != null) ?? null;
}

/** Longueur actuelle (jours) de chaque phase réglable qui a une date et une phase datée après elle. */
export function phaseLengths(phases: Partial<Record<PhaseKey, string | null>>): PhaseLengths {
  const lengths: PhaseLengths = {};
  for (const key of LENGTH_PHASE_KEYS) {
    const start = phases[key];
    const next = nextDatedPhase(phases, key);
    if (start && next) lengths[key] = toDay(phases[next]!) - toDay(start);
  }
  return lengths;
}

/** Durées du planning pour un projet : les siennes (lot O) quand il en a, sinon celles des Réglages. */
export function effectiveDurations(
  settings: PlanningDurations,
  overrides: ProjectDurationOverrides | null | undefined,
): PlanningDurations {
  return { ...settings, ...(overrides ?? {}) };
}

function clampWeeks(weeks: number): number {
  return Math.min(MAX_INTEGRATION_WEEKS, Math.max(1, Math.round(nonNegative(weeks))));
}

export interface ResizeInput extends Omit<ChainInput, 'durations'> {
  /** Dates actuelles du projet (clés de `projects.phases`). */
  phases: Partial<Record<PhaseKey, string | null>>;
  /** Nouvelles longueurs en jours ; une longueur égale à l'actuelle, ou d'une phase sans suite, est ignorée. */
  lengths?: PhaseLengths;
  /** Nouvelle durée d'intégration en semaines (`integrationWeeks` = l'actuelle). */
  newIntegrationWeeks?: number;
  /** Rendez-vous fixés (date dans l'agenda) : ils ne bougent jamais. */
  fixed?: readonly MeetingKey[];
}

export interface ResizeChange {
  /** Phase dont la longueur change ; `Integration` : durée en semaines. */
  key: LengthPhaseKey | 'Integration';
  from: number;
  to: number;
}

export interface ResizeResult {
  /** Nouvelles dates (objet complet, `null` = pas de phase). */
  phases: Record<PhaseKey, string | null>;
  integrationWeeks: number;
  /** Longueurs réellement changées, dans l'ordre des phases. */
  changes: ResizeChange[];
  /** Durées propres au projet à retenir (seulement celles qui correspondent sans ambiguïté à un réglage). */
  durations: ProjectDurationOverrides;
  /** Explications du placement (congés, intégration décalée…), comme la simulation. */
  notes: string[];
  /** Points d'attention : rendez-vous fixé percuté, phase pendant des congés, semaine fermée. */
  warnings: string[];
  /** Lot BQ : rendez-vous fixés percutés (le calcul les dépasse) : le plan reste à valider à l'écran. */
  fixedHits: MeetingKey[];
  /** Lot BP : semaines où l'intégration arrive au-dessus de la capacité (alerte, le déplacement est accepté). */
  overloads: IntegrationOverload[];
  delayedWeeks: number;
  /**
   * Lot BJ : longueurs demandées sans effet, retirées de `changes` et de `durations` : la phase est suivie de
   * l'intégration, qui démarre toujours un lundi (ou dont la semaine visée est refusée) et ne bouge donc pas.
   */
  ignored: ResizeIgnored[];
  /**
   * Lot BJ : placement de l'intégration quand son lundi au plus tôt a changé (null : elle a gardé sa date sans
   * calcul). Un lundi plus tôt dont la semaine est refusée ne la repousse jamais : elle garde alors sa date.
   */
  integration: ResizeIntegration | null;
}

/** Lot BJ : une longueur demandée qui ne change rien (voir `ResizeResult.ignored`). */
export interface ResizeIgnored {
  key: LengthPhaseKey;
  /** Longueur actuelle (jours), qui reste. */
  from: number;
  /** Longueur demandée (jours). */
  asked: number;
  /** Date de l'intégration, inchangée. */
  integration: string;
  /** Lundi où l'intégration aurait démarré. */
  target: string;
  /** Semaine visée refusée (null : c'est le même lundi que l'actuel). */
  block: IntegrationWeekBlock | null;
}

/** Lot BJ : où l'intégration a été placée par un changement de durée. */
export interface ResizeIntegration {
  /** Lundi au plus tôt d'après les nouvelles durées. */
  target: string;
  /** Lundi retenu (après les semaines refusées). */
  start: string;
  /** Première semaine refusée quand `start` n'est pas `target`. */
  block: IntegrationWeekBlock | null;
}

/**
 * Changer la durée d'une ou plusieurs phases d'un projet (lot O) : les phases avant le premier changement ne
 * bougent pas ; chaque phase suivante part de sa date actuelle, décalée comme la phase qui la précède (son
 * écart avec elle est gardé, sauf si c'est justement la longueur changée), puis les règles du planning
 * s'appliquent :
 * - R3, R4 : jour ouvré hors congés de la personne des rendez-vous ; R5 : de même, hors semaines fermées ;
 * - maquette : après les congés de la maquettiste qui la chevauchent (sauf sous-traitance), jour ouvré, hors semaine
 *   fermée ; la limite de maquettes par semaine ne la repousse plus, `overloads` donne la semaine à signaler ;
 * - intégration : lundi au plus tôt, puis la première semaine sans fermeture et sans congé de la personne de
 *   l'intégration. Lot BP (décision de Maxime du 02/11/2026) : la capacité, habituelle ou maximale, ne la repousse
 *   ni ne la refuse plus, `overloads` donne les semaines à signaler ;
 * - un rendez-vous fixé (`fixed`) garde sa date ; si le calcul le dépasse, un avertissement le dit, et la suite
 *   repart de lui ;
 * - lot BJ : la longueur de la phase suivie de l'intégration est arrondie au lundi visé ; même lundi qu'avant :
 *   l'intégration garde sa date ; un lundi plus tôt dont la semaine est refusée ne la repousse jamais (elle garde
 *   sa date) ; une longueur qui ne la fait pas bouger n'est pas retenue (`ignored`) ;
 * - une phase dont le point de départ ne change pas garde sa date (aucune règle réappliquée).
 * Un projet importé sans période ni rendez-vous fixé voit simplement ses dates suivantes bouger. `existing` ne
 * doit pas contenir l'intégration du projet lui-même.
 */
export function resizeProject(input: ResizeInput, options: SimulateOptions = {}): ResizeResult {
  const oldWeeks = clampWeeks(input.integrationWeeks);
  const newWeeks = input.newIntegrationWeeks === undefined ? oldWeeks : clampWeeks(input.newIntegrationWeeks);
  const c = chainContext(
    { ...input, durations: DEFAULT_PLANNING_DURATIONS, integrationWeeks: newWeeks },
    options,
  );
  const fixed = new Set(input.fixed ?? []);
  const old = fullPhases(input.phases);
  const current = phaseLengths(old);

  const requested: PhaseLengths = {};
  const changes: ResizeChange[] = [];
  for (const key of LENGTH_PHASE_KEYS) {
    const want = input.lengths?.[key];
    const have = current[key];
    if (want === undefined || have === undefined) continue;
    const value = Math.round(nonNegative(want));
    if (value === have) continue;
    requested[key] = value;
    changes.push({ key, from: have, to: value });
  }
  if (newWeeks !== oldWeeks) changes.push({ key: 'Integration', from: oldWeeks, to: newWeeks });

  const out = { ...old };
  const warnings: string[] = [];
  if (changes.length === 0) {
    return {
      phases: out,
      integrationWeeks: oldWeeks,
      changes,
      durations: {},
      notes: [],
      warnings,
      fixedHits: [],
      overloads: [],
      delayedWeeks: 0,
      ignored: [],
      integration: null,
    };
  }
  const fixedHits: MeetingKey[] = [];
  const asked: PhaseLengths = { ...requested };
  const startIndex = Math.min(...changes.map((change) => PHASE_KEYS.indexOf(change.key)));

  let prev: { key: PhaseKey; old: number; now: number } | null = null;
  let placed: PlacedIntegration | null = null;
  let integrationInfo: { target: number; start: number; block: IntegrationWeekBlock | null } | null = null;
  for (const key of PHASE_KEYS) {
    if (old[key] == null) continue;
    const oldDay = toDay(old[key]);
    if (PHASE_KEYS.indexOf(key) <= startIndex || prev === null) {
      prev = { key, old: oldDay, now: oldDay };
      continue;
    }
    const oldLength: number = prev.key === 'Integration' ? 7 * oldWeeks : oldDay - prev.old;
    const newLength: number =
      prev.key === 'Integration' ? 7 * newWeeks : (requested[prev.key as LengthPhaseKey] ?? oldLength);
    const earliest: number = oldDay + (prev.now - prev.old) + (newLength - oldLength);

    let day: number;
    if (MEETING_PHASES.has(key) && fixed.has(key as MeetingKey)) {
      day = oldDay;
      if (earliest > oldDay) {
        fixedHits.push(key as MeetingKey);
        warnings.push(
          `${key} fixé le ${shortDay(oldDay)} : il ne bouge pas (le calcul le placerait le ${shortDay(earliest)})`,
        );
      }
    } else if (earliest === oldDay) {
      day = oldDay;
    } else if (key === 'R3' || key === 'R4') {
      day = workDay(c.absences, earliest, c.meetings);
      if (isOff(c.absences, c.meetings, earliest)) {
        c.notes.push(`${key} décalé, ${c.name(c.meetings)} en congés`);
      }
    } else if (key === 'Maquette') {
      const notesBefore = c.notes.length;
      // Déplacement voulu : comme l'intégration, la limite de la semaine alerte (`overloads`), elle ne repousse plus.
      const placedAt = { ...c, mockupLoad: new Map<number, number>() };
      day = placeMockupFrom(placedAt, earliest, requested.Maquette ?? current.Maquette ?? c.d2);
      // Lot BJ : une maquette demandée plus tôt n'est jamais repoussée (fermeture, congés), elle garde sa date.
      if (earliest < oldDay && day > oldDay) {
        day = oldDay;
        c.notes.splice(notesBefore);
        warnings.push(`maquette gardée le ${shortDay(oldDay)} : aucune semaine plus tôt n'a de place`);
      }
    } else if (key === 'Integration') {
      // Lot BJ : l'intégration démarre un lundi ; la longueur demandée de la phase d'avant est arrondie à ce lundi.
      // Même lundi qu'avant : aucune règle réappliquée (une semaine déjà chargée ne la repousse pas).
      const target = mondayOnOrAfterDay(earliest);
      const before = prev.key as LengthPhaseKey;
      if (requested[before] !== undefined) requested[before] = requested[before] + (target - earliest);
      if (target === oldDay) {
        day = oldDay;
        integrationInfo = { target, start: oldDay, block: null };
      } else {
        // Lot BP : déplacement voulu : la charge de la semaine alerte (`overloads`), elle ne repousse plus.
        const tried = placeIntegrationFrom({ ...c, load: new Map() }, target);
        const block = tried.integration !== target ? tried.block : null;
        if (target < oldDay && tried.integration >= oldDay) {
          // Plus tôt demandé mais semaine refusée : l'intégration n'est jamais repoussée, elle garde sa date.
          day = oldDay;
          integrationInfo = { target, start: oldDay, block };
        } else {
          placed = tried;
          day = placed.integration;
          integrationInfo = { target, start: day, block };
        }
      }
    } else if (key === 'R5') {
      day = r5From(c, earliest);
    } else {
      day = earliest;
    }
    out[key] = fromDay(day);
    prev = { key, old: oldDay, now: day };
  }
  if (placed) finishNotes(c, placed);

  // Lot BJ : une longueur suivie de l'intégration qui ne bouge pas ne change rien : elle n'est pas retenue.
  const ignored: ResizeIgnored[] = [];
  for (const key of LENGTH_PHASE_KEYS) {
    const have = current[key];
    if (requested[key] === undefined || have === undefined) continue;
    if (nextDatedPhase(old, key) !== 'Integration' || !out[key] || !out.Integration) continue;
    if (!integrationInfo || toDay(out.Integration) - toDay(out[key]) !== have) continue;
    ignored.push({
      key,
      from: have,
      asked: asked[key] ?? have,
      integration: out.Integration,
      target: fromDay(integrationInfo.target),
      block: integrationInfo.block,
    });
    delete requested[key];
  }
  // Longueurs retenues, arrondies au lundi de l'intégration quand elle suit.
  changes.splice(0, changes.length);
  for (const key of LENGTH_PHASE_KEYS) {
    const have = current[key];
    const value = requested[key];
    if (value !== undefined && have !== undefined && value !== have)
      changes.push({ key, from: have, to: value });
  }
  if (newWeeks !== oldWeeks) changes.push({ key: 'Integration', from: oldWeeks, to: newWeeks });

  // Intégration allongée sans être replacée : les semaines ajoutées sont-elles libres ?
  if (newWeeks > oldWeeks && startIndex === PHASE_KEYS.indexOf('Integration') && old.Integration) {
    const start = mondayOfDay(toDay(old.Integration));
    for (let k = oldWeeks; k < newWeeks; k++) {
      // Les congés sont signalés plus bas, avec les autres phases touchées.
      const why = weekBlock(c, start + 7 * k, false);
      // Lot BP : une semaine au-dessus de la capacité est dans `overloads`, avec son nombre d'intégrations.
      if (why && why.kind !== 'full') warnings.push(`intégration allongée : ${why.text}`);
    }
  }

  // Lot AE : R3 trop loin de sa maquette (on oublie les infos précises pour la faire).
  if (out.R3 && out.Maquette && out.Maquette !== old.Maquette) {
    const gap = toDay(out.Maquette) - toDay(out.R3);
    if (gap > 7 * c.r3MaxWeeks) {
      const state = fixed.has('R3') ? 'fixé' : 'prévu';
      warnings.push(
        `R3 ${state} le ${shortDay(toDay(out.R3))} : la maquette commencerait ${gapLabel(gap)} après, ` +
          `plus de ${c.r3MaxWeeks} semaine${c.r3MaxWeeks > 1 ? 's' : ''} (les infos du R3 risquent d'être oubliées)`,
      );
    }
  }

  // Phases touchées qui tombent pendant les congés de la personne qui les fait.
  const touched = new Set<PhaseKey>([
    ...PHASE_KEYS.filter((key) => out[key] !== old[key]),
    ...changes.map((change) => change.key),
  ]);
  const clashes = conflicts(
    { phases: out, integrationWeeks: newWeeks, subcontracted: input.subcontracted },
    input.roles,
    input.absences,
  ).filter((conflict) => touched.has(conflict.phase));
  for (const clash of clashes) {
    warnings.push(
      `${PHASE_WORDS[clash.phase]} pendant les congés de ${c.name(clash.userId)} (${clash.absence.label})`,
    );
  }

  return {
    phases: out,
    integrationWeeks: newWeeks,
    changes,
    durations: overridesOf(old, current, requested),
    notes: c.notes,
    warnings,
    fixedHits,
    overloads: [
      ...mockupOverloads({
        start: out.Maquette,
        subcontracted: input.subcontracted,
        existing: input.existingMockups ?? [],
        perWeek: c.mockupsPerWeek,
        before: old.Maquette,
      }),
      ...integrationOverloads({
        start: out.Integration,
        weeks: newWeeks,
        existing: input.existing,
        capacity: input.capacity,
        before: { start: old.Integration, weeks: oldWeeks },
      }),
    ],
    delayedWeeks: placed?.delayedWeeks ?? 0,
    ignored,
    integration: integrationInfo
      ? {
          target: fromDay(integrationInfo.target),
          start: fromDay(integrationInfo.start),
          block: integrationInfo.block,
        }
      : null,
  };
}

/**
 * Lot AE : maquette placée à partir de `earliest` : après les congés de la maquettiste qui la chevauchent (sauf
 * sous-traitance), jour ouvré ; si sa semaine est fermée ou a déjà ses `mockupsPerWeek` maquettes (hors
 * sous-traitance), elle passe au lundi suivant. `resizeProject` l'appelle avec une charge vide : la limite n'y
 * repousse plus un déplacement à la main.
 */
function placeMockupFrom(c: ChainContext, earliest: number, length: number): number {
  let mockup = earliest;
  const full: number[] = [];
  for (let tries = 0; tries < MAX_MOCKUP_TRIES; tries++) {
    let absence: DayAbsence | null;
    let guard = 0;
    while ((absence = overlapAbsence(c.absences, c.mockupBy, mockup, mockup + length)) && guard++ < 10) {
      mockup = absence.to + 1;
      c.notes.push(`maquette après les congés de ${c.name(absence.userId)}`);
    }
    mockup = workDay(c.absences, mockup, c.mockupBy);
    const monday = mondayOfDay(mockup);
    const blocked = mockupWeekBlocked(c, monday);
    if (!blocked) break;
    if (blocked.kind === 'full') full.push(monday);
    else c.notes.push(blocked.text);
    mockup = monday + 7;
  }
  if (full.length > 0) c.notes.push(`maquette décalée : ${fullWeeksNote(full, c.mockupsPerWeek)}`);
  return mockup;
}

/** Durées du planning qui correspondent sans ambiguïté aux longueurs changées (`projects.durations`). */
function overridesOf(
  phases: Record<PhaseKey, string | null>,
  current: PhaseLengths,
  requested: PhaseLengths,
): ProjectDurationOverrides {
  const length = (key: LengthPhaseKey) => requested[key] ?? current[key] ?? 0;
  const asked = (key: LengthPhaseKey) => requested[key] !== undefined;
  const next = (key: PhaseKey) => nextDatedPhase(phases, key);
  const durations: ProjectDurationOverrides = {};
  if (asked('R3') && next('R3') === 'Maquette') durations.r3ToMockup = length('R3');
  if (asked('Maquette') || asked('Ajustements')) {
    if (next('Maquette') === 'R4') durations.mockupToR4 = length('Maquette');
    else if (phases.Maquette && next('Maquette') === 'Ajustements' && next('Ajustements') === 'R4') {
      durations.mockupToR4 = length('Maquette') + length('Ajustements');
    }
  }
  if (asked('R4') && next('R4') === 'Retours') durations.r4ToFeedback = length('R4');
  if (asked('Retours') && next('Retours') === 'Integration')
    durations.feedbackToIntegration = length('Retours');
  return durations;
}

/* ------------------------------------------------------------------ retours raccourcis (lot BJ) */

/** Entrée d'un atterrissage : celle de `resizeProject`, sans les nouvelles longueurs. */
export type LandingInput = Omit<ResizeInput, 'lengths' | 'newIntegrationWeeks'>;

/** Lot BJ : où atterrit l'intégration quand on change la longueur de la phase qui la précède. */
export interface IntegrationLanding {
  phase: LengthPhaseKey;
  /** Longueur à demander (jours), arrondie au lundi visé : du début de la phase au lundi `target`. */
  length: number;
  /** Date actuelle de l'intégration. */
  current: string;
  /** Lundi visé. */
  target: string;
  /** Date où elle démarrerait vraiment (après les semaines refusées). */
  start: string;
  /** Écart en jours entre `start` et `current` (−7 : une semaine plus tôt). */
  days: number;
  /** Semaine visée refusée (fermeture, congés de la personne de l'intégration) ; null : libre. */
  block: IntegrationWeekBlock | null;
  /** Lot BP : semaines au-dessus de la capacité où elle arriverait (alerte, jamais un refus). */
  overloads: IntegrationOverload[];
  /** L'intégration bouge-t-elle vraiment ? */
  moved: boolean;
}

/**
 * Lot BJ : « où atterrit l'intégration si je tire la phase `phase` jusqu'à `length` jours ? ». Seulement pour une
 * phase dont la phase datée suivante est l'intégration (sinon null). Même calcul que `resizeProject` (lundi au
 * plus tôt, puis la première semaine libre), donc le même résultat que le plan `project.resize` côté serveur.
 */
export function integrationLanding(
  input: LandingInput,
  phase: LengthPhaseKey,
  length: number,
  options: SimulateOptions = {},
): IntegrationLanding | null {
  const start = input.phases[phase];
  const current = input.phases.Integration;
  if (!start || !current || nextDatedPhase(input.phases, phase) !== 'Integration') return null;
  const begin = toDay(start);
  const target = mondayOnOrAfterDay(begin + Math.max(1, Math.round(nonNegative(length))));
  const snapped = target - begin;
  const base = { phase, length: snapped, current, target: fromDay(target) };
  if (target === toDay(current)) {
    return { ...base, start: current, days: 0, block: null, overloads: [], moved: false };
  }
  const result = resizeProject({ ...input, lengths: { [phase]: snapped } }, options);
  const landed = result.phases.Integration ?? current;
  return {
    ...base,
    start: landed,
    days: toDay(landed) - toDay(current),
    block: result.integration?.block ?? null,
    overloads: result.overloads,
    moved: landed !== current,
  };
}

/**
 * Lot BJ : premier lundi, en s'éloignant de l'intégration actuelle semaine par semaine (`direction` −1 : plus tôt),
 * où l'intégration démarrerait vraiment, sans semaine refusée ; null si aucun dans `maxWeeks` semaines (ou si la
 * phase deviendrait trop courte).
 */
export function nextIntegrationLanding(
  input: LandingInput,
  phase: LengthPhaseKey,
  direction: -1 | 1,
  options: SimulateOptions = {},
  maxWeeks = 8,
): IntegrationLanding | null {
  const start = input.phases[phase];
  const current = input.phases.Integration;
  if (!start || !current) return null;
  const monday = mondayOfDay(toDay(current));
  for (let k = 1; k <= maxWeeks; k++) {
    const length = monday + direction * 7 * k - toDay(start);
    if (length < 1) return null;
    const landing = integrationLanding(input, phase, length, options);
    if (!landing) return null;
    if (landing.moved && !landing.block) return landing;
  }
  return null;
}

const LENGTH_OBJECTS: Record<LengthPhaseKey, string> = {
  R3: 'le délai après le R3',
  Maquette: 'la maquette',
  Ajustements: 'les ajustements',
  R4: 'le délai après le R4',
  Retours: 'les retours',
};

/** Fin de phrase : le point final, sauf après une abréviation (« 2 nov. »). */
function end(text: string): string {
  return text.endsWith('.') ? text : `${text}.`;
}

/** « d'une semaine », « de 2 semaines », « de 3 jours ». */
function byHowMuch(days: number): string {
  const n = Math.abs(days);
  if (n % 7 === 0) return n === 7 ? "d'une semaine" : `de ${n / 7} semaines`;
  return `de ${n} jour${n > 1 ? 's' : ''}`;
}

/**
 * Lot BJ : pourquoi une longueur demandée ne change rien, en français, avec la semaine qui marcherait :
 * « L'intégration démarre toujours un lundi : raccourcir les retours de 3 jours ne change rien (elle reste
 * le lun. 2 nov.). Raccourcis les retours d'une semaine pour la démarrer le lun. 26 oct. » ; « Raccourcir les retours d'une semaine ne change rien :
 * 2 intégrations déjà prévues la semaine du lun. 26 oct., l'intégration reste le lun. 2 nov. »
 */
export function ignoredResizeText(ignored: ResizeIgnored, suggestion: IntegrationLanding | null): string {
  const shorter = ignored.asked < ignored.from;
  const verb = shorter ? 'raccourcir' : 'allonger';
  const what = `${verb} ${LENGTH_OBJECTS[ignored.key]} ${byHowMuch(ignored.asked - ignored.from)}`;
  const integration = shortDay(toDay(ignored.integration));
  const head = ignored.block
    ? `${what.charAt(0).toUpperCase()}${what.slice(1)} ne change rien : ${ignored.block.text}, l'intégration reste le ${end(integration)}`
    : `L'intégration démarre toujours un lundi : ${what} ne change rien (elle reste le ${integration}).`;
  if (!suggestion) {
    return `${head} Aucune semaine ${shorter ? 'plus tôt' : 'plus tard'} n'est possible pour l'intégration.`;
  }
  const weeks = Math.round(Math.abs(toDay(suggestion.target) - mondayOfDay(toDay(ignored.integration))) / 7);
  const imperative = shorter ? 'Raccourcis' : 'Allonge';
  return `${head} ${imperative} ${LENGTH_OBJECTS[ignored.key]} ${byHowMuch(7 * weeks)} pour la démarrer le ${end(shortDay(toDay(suggestion.start)))}`;
}

/** Lot BJ : la phase `phase` est-elle suivie (première phase datée après elle) de l'intégration ? */
export function precedesIntegration(
  phases: Partial<Record<PhaseKey, string | null>>,
  phase: PhaseKey,
): boolean {
  return phase !== 'Integration' && !!phases[phase] && nextDatedPhase(phases, phase) === 'Integration';
}

/* ------------------------------------------------------------------ plusieurs devis et date limite (lot BL) */

/**
 * Lot BL : avertissement de l'écran de validation quand le R5 calculé dépasse la date « en ligne au plus tard » du
 * client (jamais de blocage) ; null : pas de date limite, pas de R5, ou date tenue.
 */
export function onlineByWarning(
  r5: string | null | undefined,
  onlineBy: string | null | undefined,
): string | null {
  if (!r5 || !onlineBy || r5 <= onlineBy) return null;
  return `mise en ligne le ${shortDay(toDay(r5))}, après la date limite du client (${shortDay(toDay(onlineBy))})`;
}

/** Lot BL : résultat d'un devis simulé face à sa date limite. */
export type SimulationStatus = 'fits' | 'big_week' | 'late' | 'none';

/** Lot BL : un devis de la simulation, avec ses réglages actuels (dans l'ordre de la liste). */
export interface SimulateManyQuote {
  /** Identifiant rendu tel quel (le devis). */
  key: string;
  integrationWeeks: number;
  subcontracted: boolean;
  /** Démarrage au plus tôt (« dans 1 mois », date précise). */
  startNotBefore?: string;
  /** Projet partenaire (intégration puis R5) : signature et délai ; absent ou null : projet Evolusite. */
  partner?: { signedOn: string | null; delayDays: number } | null;
  /** Date « en ligne au plus tard » ; absente : pas de date limite (statut `none`). */
  onlineBy?: string | null;
}

/** Lot BL : un projet existant qui a une date limite (il ne bouge pas dans la simulation). */
export interface SimulateManyDeadline {
  key: string;
  onlineBy: string;
  r5: string | null;
}

export interface SimulateManyInput extends Omit<
  SimulateInput,
  'integrationWeeks' | 'subcontracted' | 'startNotBefore'
> {
  items: readonly SimulateManyQuote[];
  /** « Accepter des grosses semaines » : la capacité maximale des Réglages, seulement pour tenir une date limite. */
  allowBigWeeks: boolean;
  existingDeadlines?: readonly SimulateManyDeadline[];
  /** Période de la ligne de charge (par défaut : d'aujourd'hui au dernier R5 simulé, 26 semaines au moins). */
  range?: { from: string; to: string };
}

export interface SimulateManyPlacement {
  key: string;
  /** Dates du projet simulé (clés de `projects.phases`) ; un projet partenaire n'a que l'intégration et le R5. */
  phases: Partial<Record<PhaseKey, string | null>>;
  integration: string;
  r5: string;
  status: SimulationStatus;
  /** Notes du planning (congés, semaines pleines…) et de la date limite, en français. */
  notes: string[];
  onlineBy: string | null;
  /** Semaines d'intégration (lundis) passées au-dessus de la capacité habituelle (statut `big_week`). */
  bigWeeks: string[];
  /** Mise en ligne sans grosse semaine (statut `big_week`), sinon null. */
  withoutBigWeekR5: string | null;
}

export interface SimulateManyOutput {
  placements: SimulateManyPlacement[];
  /** Intégrations par semaine, projets existants et simulés compris (`max` : capacité maximale des Réglages). */
  weekLoad: { monday: string; count: number; max: number }[];
  /** Projets existants avec une date limite : tient ou dépasse (ils ne bougent pas). */
  existing: { key: string; onlineBy: string; r5: string | null; status: SimulationStatus }[];
}

interface PlacedDraft {
  phases: Partial<Record<PhaseKey, string | null>>;
  integration: string;
  mockup: string | null;
  r5: string;
  notes: string[];
}

/**
 * Lot BL : placer plusieurs devis dans l'ordre de la liste, projets existants compris (ils ne bougent pas). Chaque
 * devis est simulé comme un nouveau projet (`simulate`, ou `simulatePartner` pour un projet partenaire) avec la charge
 * des projets existants et des devis placés avant lui (intégrations et maquettes), puis comparé à sa date limite :
 * - pas de date limite : placé à la capacité habituelle, statut `none` ;
 * - mise en ligne avant (ou le jour de) la date limite à la capacité habituelle : `fits` ;
 * - sinon, si les grosses semaines sont acceptées et que la capacité maximale des Réglages tient la date : `big_week`
 *   (placé ainsi, `bigWeeks` = ses semaines au-dessus de l'habituel, `withoutBigWeekR5` = la date sans) ;
 * - sinon `late`, placé à la capacité habituelle (une grosse semaine n'est jamais prise pour rien), avec la date
 *   qu'une grosse semaine donnerait dans les notes quand elle avance la mise en ligne.
 */
export function simulateMany(input: SimulateManyInput, options: SimulateOptions = {}): SimulateManyOutput {
  const usual = Math.max(1, input.capacity.usual);
  const max = Math.max(usual, input.capacity.max);
  const extraIntegrations: ExistingIntegration[] = [];
  const extraMockups: ExistingMockup[] = [];
  const placements: SimulateManyPlacement[] = [];

  for (const item of input.items) {
    const weeks = Math.min(3, Math.max(1, Math.round(item.integrationWeeks))) as 1 | 2 | 3;
    const existing = [...input.existing, ...extraIntegrations];
    const existingMockups = [...input.existingMockups, ...extraMockups];
    const run = (capacity: number): PlacedDraft => {
      const cap = { usual: capacity, max };
      if (item.partner) {
        const result = simulatePartner(
          {
            today: input.today,
            ...(item.startNotBefore ? { startNotBefore: item.startNotBefore } : {}),
            signedOn: item.partner.signedOn,
            delayDays: item.partner.delayDays,
            integrationWeeks: weeks,
            capacity: cap,
            roles: input.roles,
            absences: input.absences,
            closedWeeks: input.closedWeeks,
            existing,
          },
          options,
        );
        return {
          phases: { Integration: result.integration, R5: result.r5 },
          integration: result.integration,
          mockup: null,
          r5: result.r5,
          notes: result.notes,
        };
      }
      const result = simulate(
        {
          ...input,
          ...(item.startNotBefore ? { startNotBefore: item.startNotBefore } : {}),
          integrationWeeks: weeks,
          subcontracted: item.subcontracted,
          capacity: cap,
          existing,
          existingMockups,
        },
        options,
      );
      return {
        phases: {
          R3: result.r3,
          Maquette: result.mockup,
          R4: result.r4,
          Retours: result.feedback,
          Integration: result.integration,
          R5: result.r5,
        },
        integration: result.integration,
        mockup: result.mockup,
        r5: result.r5,
        notes: result.notes,
      };
    };

    const atUsual = run(usual);
    const onlineBy = item.onlineBy ?? null;
    let chosen = atUsual;
    let status: SimulationStatus = 'none';
    let withoutBigWeekR5: string | null = null;
    const extraNotes: string[] = [];
    if (onlineBy) {
      if (atUsual.r5 <= onlineBy) {
        status = 'fits';
      } else {
        const atMax = max > usual ? run(max) : atUsual;
        if (input.allowBigWeeks && atMax.r5 <= onlineBy) {
          status = 'big_week';
          chosen = atMax;
          withoutBigWeekR5 = atUsual.r5;
        } else {
          status = 'late';
          if (atMax.r5 < atUsual.r5) {
            const when = shortDay(toDay(atMax.r5));
            extraNotes.push(
              input.allowBigWeeks
                ? `même avec une grosse semaine : en ligne le ${when}, après sa date limite`
                : atMax.r5 <= onlineBy
                  ? `avec une grosse semaine (${max} intégrations) : en ligne le ${when}, avant sa date limite`
                  : `avec une grosse semaine : en ligne le ${when}, encore après sa date limite`,
            );
          }
        }
      }
    }

    // Semaines de l'intégration retenue au-dessus de l'habituel (charge avant ce devis).
    const before = loadByMondayDay(existing);
    const bigWeeks = integrationMondayDays(chosen.integration, weeks)
      .filter((monday) => (before.get(monday) ?? 0) >= usual)
      .map(fromDay);

    placements.push({
      key: item.key,
      phases: chosen.phases,
      integration: chosen.integration,
      r5: chosen.r5,
      status,
      notes: [...chosen.notes, ...extraNotes],
      onlineBy,
      bigWeeks: status === 'big_week' ? bigWeeks : [],
      withoutBigWeekR5,
    });
    extraIntegrations.push({ integrationStart: chosen.integration, integrationWeeks: weeks });
    if (chosen.mockup) extraMockups.push({ mockupStart: chosen.mockup, subcontracted: item.subcontracted });
  }

  const lastR5 = placements.reduce((last, p) => (p.r5 > last ? p.r5 : last), input.today);
  const minEnd = fromDay(toDay(input.today) + 7 * 26);
  const range = input.range ?? { from: input.today, to: lastR5 > minEnd ? lastR5 : minEnd };
  const load = weekLoad([...input.existing, ...extraIntegrations], range);
  return {
    placements,
    weekLoad: Object.entries(load).map(([monday, count]) => ({ monday, count, max })),
    existing: (input.existingDeadlines ?? []).map((d) => ({
      key: d.key,
      onlineBy: d.onlineBy,
      r5: d.r5,
      status: d.r5 == null ? 'none' : d.r5 <= d.onlineBy ? 'fits' : 'late',
    })),
  };
}
